import { Empty, Tabs } from 'antd';
import React, { useEffect, useState } from 'react';
import { css } from '@emotion/css';
import { GenericObject } from '../../utils/types';
import ProfileData from './ProfileData';
import SearchHistory from './SearchHistory';
import {
  ISearchFavoritesConfigInterface,
  removeItemFromLocalStorage,
  setItemToLocalStorage,
} from '../../utils/constants';
import { get } from 'lodash';
import SearchFavorites from './SearchFavorites';
import SavedSearches from './SavedSearches';
const { TabPane } = Tabs;

const container = css`
  padding: 1rem;
`;

const useConstructor = (callBack = () => {}) => {
  const [hasBeenCalled, setHasBeenCalled] = useState(false);
  if (hasBeenCalled) return;
  callBack();
  setHasBeenCalled(true);
};

const ProfilePageTabs = {
  PROFILE_DATA: 'Profile Data',
  SEARCH_HISTORY: 'Search History',
  SEARCH_FAVORITES: 'Search Favorites',
  SAVED_SEARCHES: 'Saved Searches',
} as const;

type ReverseMap<T> = T[keyof T];
type ProfileTabsValues = ReverseMap<typeof ProfilePageTabs>;
export interface IProfilePageProps {
  preferences?: GenericObject;
  url: String;
  credentials: String;
  userId: String;
  tabs: Array<ProfileTabsValues>;
  showProfileData?: Boolean;
  allowProfileEdit?: Boolean;
  showThemeSettings?: Boolean;
  showSearchSettings?: Boolean;
  showCloseAccount?: Boolean;
  onProfileFetch?: (userData: GenericObject) => void;
  onProfileSave?: (updatedUserData: GenericObject) => void;
  favoritesConfig: ISearchFavoritesConfigInterface & {};
}

function ProfilePage({
  credentials,
  url,
  userId,
  showProfileData,
  allowProfileEdit,
  showThemeSettings = true,
  showSearchSettings = true,
  showCloseAccount,
  onProfileFetch,
  onProfileSave,
  tabs,
  preferences,
  favoritesConfig,
}: IProfilePageProps) {
  const { enableProfilePage, profileSettingsForm } =
    preferences instanceof Object
      ? get(preferences, 'authenticationSettings')
      : { enableProfilePage: false, profileSettingsForm: {} };
  const {
    viewData = false,
    editData = false,
    closeAccount = false,
    editThemeSettings = false,
    editSearchPreferences = false,
  } = profileSettingsForm;
  const tabsToShow = {
    PROFILE_DATA: tabs?.includes(ProfilePageTabs.PROFILE_DATA),
    SEARCH_HISTORY: tabs?.includes(ProfilePageTabs.SEARCH_HISTORY),
    SEARCH_FAVORITES: tabs?.includes(ProfilePageTabs.SEARCH_FAVORITES),
    SAVED_SEARCHES: tabs?.includes(ProfilePageTabs.SAVED_SEARCHES),
  };

  useConstructor(() => {
    setItemToLocalStorage('creds', credentials as string);
    setItemToLocalStorage('host', url as string);
    setItemToLocalStorage('user_id', userId as string);
  });
  useEffect(() => {
    return () => {
      removeItemFromLocalStorage('creds');
      removeItemFromLocalStorage('host');
      removeItemFromLocalStorage('user_id');
    };
  }, []);

  return (
    <div className={`${container} profile-page-wrapper`}>
      {!enableProfilePage ? (
        <Empty
          description={
            <span>
              Oops! The feature is disabled from the Search-UI dashboard.
            </span>
          }
        />
      ) : (
        <Tabs defaultActiveKey="1" destroyInactiveTabPane>
          {tabsToShow.PROFILE_DATA && (
            <TabPane tab={ProfilePageTabs['PROFILE_DATA']} key="1">
              {(typeof showProfileData === 'boolean' ? (
                showProfileData
              ) : (
                viewData
              )) ? (
                <ProfileData
                  allowProfileEdit={
                    typeof allowProfileEdit === 'boolean'
                      ? allowProfileEdit
                      : editData
                  }
                  showThemeSettings={showThemeSettings}
                  editThemeSettings={editThemeSettings}
                  editSearchPreferences={editSearchPreferences}
                  showSearchSettings={showSearchSettings}
                  onProfileFetch={onProfileFetch}
                  onProfileSave={onProfileSave}
                  showCloseAccount={
                    typeof showCloseAccount === 'boolean'
                      ? showCloseAccount
                      : closeAccount
                  }
                />
              ) : (
                <Empty
                  description={
                    <span>Oops! The feature is disabled by the admin.</span>
                  }
                />
              )}
            </TabPane>
          )}
          {tabsToShow.SEARCH_HISTORY && (
            <TabPane tab={ProfilePageTabs['SEARCH_HISTORY']} key="2">
              <SearchHistory />
            </TabPane>
          )}
          {tabsToShow.SEARCH_FAVORITES && (
            <TabPane tab={ProfilePageTabs['SEARCH_FAVORITES']} key="3">
              <SearchFavorites {...favoritesConfig} />
            </TabPane>
          )}{' '}
          {tabsToShow.SAVED_SEARCHES && (
            <TabPane tab={ProfilePageTabs['SAVED_SEARCHES']} key="4">
              <SavedSearches />
            </TabPane>
          )}
        </Tabs>
      )}
    </div>
  );
}

export default ProfilePage;
